import clsx from 'clsx';
import type { WNBATeam, WNBATeamMetrics } from '../../../types';

function TrendBar({
  label,
  value,
  max,
  colorCls,
}: {
  label: string;
  value: number;
  max: number;
  colorCls: string;
}) {
  const pct = Math.min(100, Math.max(0, ((value + max) / (max * 2)) * 100));
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">{label}</span>
        <span className={clsx('font-semibold tabular-nums', colorCls)}>
          {value > 0 ? '+' : ''}{value.toFixed(1)}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-4 overflow-hidden">
        <div
          className={clsx('h-full rounded-full', colorCls.replace('text-', 'bg-'))}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function TeamFormCard({
  team,
  metrics,
  isHome,
}: {
  team: WNBATeam;
  metrics?: WNBATeamMetrics;
  isHome: boolean;
}) {
  const l5 = metrics?.last5NetRating;
  const l10 = metrics?.last10NetRating;
  const net = metrics?.netRating;

  const trending = l5 !== undefined && l10 !== undefined ? l5 > l10 : null;
  const max = 15;

  return (
    <div className="rounded-lg border border-surface-4 bg-surface-2 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-semibold text-gray-200">{team.abbreviation}</span>
          <span className="text-xs text-gray-600 ml-2">{isHome ? 'Home' : 'Away'}</span>
        </div>
        {trending !== null && (
          <span className={clsx('text-xs font-semibold', trending ? 'text-win' : 'text-loss')}>
            {trending ? '↑ Hot' : '↓ Cold'}
          </span>
        )}
      </div>

      {metrics ? (
        <div className="space-y-2.5">
          {l5 !== undefined && (
            <TrendBar
              label="Last 5 Net Rtg"
              value={l5}
              max={max}
              colorCls={l5 >= 0 ? 'text-win' : 'text-loss'}
            />
          )}
          {l10 !== undefined && (
            <TrendBar
              label="Last 10 Net Rtg"
              value={l10}
              max={max}
              colorCls={l10 >= 0 ? 'text-win' : 'text-loss'}
            />
          )}
          {net !== undefined && (
            <TrendBar
              label="Season Net Rtg"
              value={net}
              max={max}
              colorCls={net >= 0 ? 'text-brand-light' : 'text-gray-500'}
            />
          )}
        </div>
      ) : (
        <div className="text-xs text-gray-600 text-center py-2">No data</div>
      )}
    </div>
  );
}

interface FormTrendPanelProps {
  awayTeam: WNBATeam;
  homeTeam: WNBATeam;
  awayTeamMetrics?: WNBATeamMetrics;
  homeTeamMetrics?: WNBATeamMetrics;
}

export function FormTrendPanel({
  awayTeam,
  homeTeam,
  awayTeamMetrics,
  homeTeamMetrics,
}: FormTrendPanelProps) {
  if (!awayTeamMetrics && !homeTeamMetrics) {
    return (
      <div className="text-center py-8 text-gray-600 text-sm">
        No form data available
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <TeamFormCard team={awayTeam} metrics={awayTeamMetrics} isHome={false} />
      <TeamFormCard team={homeTeam} metrics={homeTeamMetrics} isHome={true} />
    </div>
  );
}
